import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

/**
 * Shared state between the phone header (avatar), the bottom tab bar (Menu tab)
 * and the menu sheet. Navbar owns the account data and the account actions
 * (business info panel, sign-out confirm); it publishes them here so the sheet
 * can show them without Layout having to thread a dozen props.
 *
 *  account       primitives only (name, plan, logo, flags) - safe to put in state
 *  actionsRef    { businessInfo(), signOut(), profile(), finishSetup() } - a ref so
 *                Navbar can reassign fresh closures every render without
 *                re-publishing the account or re-rendering consumers.
 */
const NOOP_CONTEXT = {
  menuOpen: false,
  openMenu: () => {},
  closeMenu: () => {},
  toggleMenu: () => {},
  account: null,
  setAccount: () => {},
  actionsRef: { current: {} },
};

const MobileNavContext = createContext(NOOP_CONTEXT);

export const useMobileNav = () => useContext(MobileNavContext);

export function MobileNavProvider({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [account, setAccountState] = useState(null);
  const actionsRef = useRef({});

  const openMenu = useCallback(() => setMenuOpen(true), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((open) => !open), []);

  // Only re-publish when something a consumer renders actually changed.
  const setAccount = useCallback((next) => {
    setAccountState((prev) => {
      if (prev && next && Object.keys(next).every((k) => prev[k] === next[k])) return prev;
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ menuOpen, openMenu, closeMenu, toggleMenu, account, setAccount, actionsRef }),
    [menuOpen, openMenu, closeMenu, toggleMenu, account, setAccount]
  );

  return <MobileNavContext.Provider value={value}>{children}</MobileNavContext.Provider>;
}
